import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { NbDialogRef } from '@nebular/theme';
import { PlayerData, PlayerSearchResult } from '../../tournament.interface';
import { TournamentService } from '../../tournament.service';
import { debounceTime, distinctUntilChanged, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Subject, of } from 'rxjs';

@Component({
    selector: 'app-player-selection-dialog',
    templateUrl: './player-selection-dialog.component.html',
    styleUrls: ['./player-selection-dialog.component.scss'],
})
export class PlayerSelectionDialogComponent implements OnInit, OnDestroy {
    @Input() currentPlayer: PlayerData | null = null;
    @Input() tournamentId: string = '';

    players: PlayerData[] = [];
    searchTerm: string = '';
    filteredPlayers: PlayerData[] = [];
    selectedPlayer: PlayerData | null = null;
    searchResults: PlayerSearchResult[] = [];
    isSearching: boolean = false;

    private searchSubject = new Subject<string>();
    private destroy$ = new Subject<void>();

    constructor(
        private dialogRef: NbDialogRef<PlayerSelectionDialogComponent>,
        private tournamentService: TournamentService
    ) {}

    ngOnInit(): void {
        this.filteredPlayers = this.players;
        this.selectedPlayer = this.currentPlayer;

        this.searchSubject
            .pipe(
                tap(() => {
                    this.isSearching = true;
                }),
                debounceTime(300),
                distinctUntilChanged(),
                switchMap((searchTerm) => {
                    return this.tournamentService.searchPlayersFromTournament(this.tournamentId, searchTerm);
                }),
                takeUntil(this.destroy$)
            )
            .subscribe({
                next: (response) => {
                    this.isSearching = false;
                    this.searchResults = response.registers;
                    this.updateFilteredPlayers();
                },
                error: (error) => {
                    this.isSearching = false;
                    console.error('Error searching players:', error);
                    this.searchResults = [];
                    this.updateFilteredPlayers();
                },
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.searchSubject.complete();
    }

    onSearchChange(event: any): void {
        const searchValue = event.target.value;
        this.searchTerm = searchValue;

        if (searchValue.trim() === '') {
            this.searchResults = [];
            this.updateFilteredPlayers();
        } else {
            this.searchSubject.next(searchValue);
        }
    }

    private updateFilteredPlayers(): void {
        if (this.searchTerm.trim() === '') {
            this.filteredPlayers = this.players;
            return;
        }
        this.filteredPlayers = this.searchResults?.map((result) => ({
            id_site: result.id_site,
            nome_abreviado: result.nome_abreviado,
            idade: result.idade,
            nome_cidade: result.nome_cidade,
            sigla_uf: result.sigla_uf,
            avatar: result.avatar,
        }));
    }

    selectPlayer(player: PlayerData): void {
        this.selectedPlayer = player;
    }

    close(): void {
        this.dialogRef.close();
    }

    confirm(): void {
        if (this.selectedPlayer) {
            this.dialogRef.close(this.selectedPlayer);
        }
    }
}
